import type {
  ResolvedPluginManifest,
  ResolvedToolbarConfig,
  ResolvedRouteConfig,
} from "@aspect/plugin-sdk";
import { create } from "zustand";

export type PluginStatus = "loaded" | "active" | "error";

/**
 * 工具栏按钮在宿主侧的运行时状态：关联插件、manifest 中的工具栏配置，
 * 以及展示层使用的可见性、标题、图标与角标等派生字段。
 */
export interface ToolbarButtonState {
  pluginId: string;
  config: ResolvedToolbarConfig;
  visible: boolean;
  title: string;
  icon: string;
  badge: { text: string; variant?: "default" | "destructive" } | null;
}

/**
 * 路由入口在宿主侧的运行时状态：关联插件、manifest 中的路由配置，
 * 以及侧边栏/导航等展示所需的标题与图标。
 */
export interface RouteEntryState {
  pluginId: string;
  config: ResolvedRouteConfig;
  title: string;
  icon: string;
}

/**
 * 单个插件在宿主中的注册项：解析后的 manifest、资源基址，以及加载生命周期状态与错误信息。
 */
export interface PluginEntry {
  manifest: ResolvedPluginManifest;
  baseUrl: string;
  status: PluginStatus;
  error?: string;
}

/**
 * 插件 Worker 通过 `ui.openPanel` 发起的面板打开请求：目标插件、面板路径、展示形态与可选尺寸。
 */
export interface OpenPanelRequest {
  pluginId: string;
  path: string;
  display: "popup" | "modal" | "drawer";
  width?: number;
  height?: number;
}

interface PluginStoreState {
  plugins: Map<string, PluginEntry>;
  toolbarButtons: Map<string, ToolbarButtonState>;
  routeEntries: Map<string, RouteEntryState>;
  openPanel: OpenPanelRequest | null;
}

interface PluginStoreActions {
  registerPlugin(manifest: ResolvedPluginManifest, baseUrl: string): void;
  setPluginStatus(id: string, status: PluginStatus, error?: string): void;
  removePlugin(id: string): void;

  registerToolbarButton(pluginId: string, config: ResolvedToolbarConfig): void;
  updateToolbarButton(pluginId: string, updates: Partial<ToolbarButtonState>): void;
  removeToolbarButton(pluginId: string): void;

  registerRouteEntry(pluginId: string, config: ResolvedRouteConfig): void;
  removeRouteEntry(pluginId: string): void;

  requestOpenPanel(request: OpenPanelRequest): void;
  closePanel(): void;
}

/**
 * 插件运行时状态的集中存储：已注册插件、工具栏按钮、路由入口，以及待处理的面板打开请求。
 * UI 与宿主逻辑通过该 store 订阅与更新，保持插件侧与界面状态一致。
 */
export const usePluginStore = create<PluginStoreState & PluginStoreActions>((set) => ({
  plugins: new Map(),
  toolbarButtons: new Map(),
  routeEntries: new Map(),
  openPanel: null,

  /** 登记插件 manifest 与资源 baseUrl，初始状态为 `loaded`。 */
  registerPlugin(manifest, baseUrl) {
    set((state) => {
      const plugins = new Map(state.plugins);
      plugins.set(manifest.id, { manifest, baseUrl, status: "loaded" });
      return { plugins };
    });
  },

  /** 更新指定插件的生命周期状态，可选附带错误文案（如 `error` 状态）。 */
  setPluginStatus(id, status, error) {
    set((state) => {
      const plugins = new Map(state.plugins);
      const entry = plugins.get(id);
      if (entry) {
        plugins.set(id, { ...entry, status, error });
      }
      return { plugins };
    });
  },

  removePlugin(id) {
    set((state) => {
      const plugins = new Map(state.plugins);
      plugins.delete(id);
      const toolbarButtons = new Map(state.toolbarButtons);
      toolbarButtons.delete(id);
      const routeEntries = new Map(state.routeEntries);
      routeEntries.delete(id);
      return { plugins, toolbarButtons, routeEntries };
    });
  },

  /** 按插件 ID 注册工具栏按钮，从 manifest 配置填充标题、图标，角标初始为空。 */
  registerToolbarButton(pluginId, config) {
    set((state) => {
      const toolbarButtons = new Map(state.toolbarButtons);
      toolbarButtons.set(pluginId, {
        pluginId,
        config,
        visible: true,
        title: config.title,
        icon: config.icon,
        badge: null,
      });
      return { toolbarButtons };
    });
  },

  updateToolbarButton(pluginId, updates) {
    set((state) => {
      const toolbarButtons = new Map(state.toolbarButtons);
      const btn = toolbarButtons.get(pluginId);
      if (btn) {
        toolbarButtons.set(pluginId, { ...btn, ...updates });
      }
      return { toolbarButtons };
    });
  },

  removeToolbarButton(pluginId) {
    set((state) => {
      const toolbarButtons = new Map(state.toolbarButtons);
      toolbarButtons.delete(pluginId);
      return { toolbarButtons };
    });
  },

  registerRouteEntry(pluginId, config) {
    set((state) => {
      const routeEntries = new Map(state.routeEntries);
      routeEntries.set(pluginId, {
        pluginId,
        config,
        title: config.title,
        icon: config.icon,
      });
      return { routeEntries };
    });
  },

  removeRouteEntry(pluginId) {
    set((state) => {
      const routeEntries = new Map(state.routeEntries);
      routeEntries.delete(pluginId);
      return { routeEntries };
    });
  },

  /** 写入当前待打开的面板请求，由 UI 消费后渲染 popup/modal/drawer。 */
  requestOpenPanel(request) {
    set({ openPanel: request });
  },

  closePanel() {
    set({ openPanel: null });
  },
}));
